<template>
    <div id="folders-list" class="page-content">
        <!-- Spinner On load -->
        <spinner v-if="folders.length == 0 && loading" class="spinner" />

        <!-- If no Folders -->
        <p v-if="folders.length == 0 && !loading" class="empty-message">
            {{ $t('folders.none') }}
        </p>

        <!-- Conversation items -->
        <transition-group name="flip-list" tag="div">
            <component :is="'FolderItem'" v-for="folder in folders" :key="folder.hash" :folder-data="folder" :unread="unreadFolderIds.includes(String(folder.device_id))" />
        </transition-group>

        <!-- PULS-1424: as Android's "Create a Folder" in Manage Folders -->
        <button v-mdl tag="button" class="create-folder mdl-button mdl-js-button mdl-button--fab mdl-js-ripple-effect mdl-button--colored" :title="$t('folders.create')" :style="{ background: $store.state.colors_accent }" @click="createFolder">
            <i class="material-icons md-light">add</i>
        </button>
    </div>
</template>

<script>

import Hash from 'object-hash';
import { Api } from '@/utils';
import { isPremium } from '@/utils/premium.js';
import FolderItem from './FolderItem.vue';
import Spinner from '@/components/Spinner.vue';

export default {
    name: 'Folders',

    components: {
        FolderItem,
        Spinner
    },

    data () {
        return {
            title: "",
            loading: true,
            folders: [],
            unreadFolderIds: [],
        };
    },

    mounted () {
        this.$store.state.msgbus.$on('refresh-btn', this.refresh);
        this.$store.state.msgbus.$on('foldersChanged', this.fetchFolders);

        this.fetchFolders();

        // Construct colors object from saved global theme
        const colors = {
            'default': this.$store.state.theme_global_default,
            'dark': this.$store.state.theme_global_dark,
            'accent': this.$store.state.theme_global_accent,
        };

        // Commit them to current application colors
        this.$store.commit('colors', colors);
    },

    beforeDestroy () {
        this.$store.state.msgbus.$off('refresh-btn', this.refresh);
        this.$store.state.msgbus.$off('foldersChanged', this.fetchFolders);
    },

    methods: {

        fetchFolders () {
            Api.folders.get()
                .then(response => this.processFolders(response));
            this.fetchUnreadFolders();
        },

        // Same rule as Android (PULS-1324): a folder is unread if it holds at least one
        // unread, non-archived, non-private conversation. index_public_unread already
        // excludes read and private conversations, but not archived ones, and it's never
        // cached, so this always reflects the server.
        fetchUnreadFolders () {
            Api.conversations.getList('index_public_unread')
                .then(conversations => {
                    const ids = new Set();
                    for (const conversation of conversations) {
                        if (conversation && !conversation.archive &&
                                conversation.folder_id != null && conversation.folder_id !== -1) {
                            ids.add(String(conversation.folder_id));
                        }
                    }
                    this.unreadFolderIds = Array.from(ids);
                })
                .catch(() => { }); // No dots is a safe fallback
        },

        processFolders (response) {
            const renderList = [];

            for(let i = 0; i < response.length; i++) {
                const item = response[i];
                item.hash = Hash(item);

                renderList.push(item);
            }

            this.folders = renderList;
            this.loading = false;

            this.$store.commit("loading", false);
            this.$store.commit('title', this.title);
        },

        // Android only lets premium accounts create folders (FolderUtils.promptCreateNewFolder).
        createFolder () {
            if (!isPremium(this.$store.state.subscription_type)) {
                this.$dialog.alert(this.$t('folders.premium'), { okText: this.$t('dialog.close'), animation: 'fade' })
                    .catch(() => { });
                return;
            }

            this.$router.push({ name: 'create-folder' });
        },

        refresh () {
            this.loading = true;
            this.fetchFolders();
        }
    }
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
    @import "../../assets/scss/_vars.scss";

    .empty-message {
        color: rgba(0, 0, 0, 0.54);
        margin: 6em auto;
        max-width: 20em;
        padding: 0 16px;
        text-align: center;
    }

    #folders-list {
        width: 100%;
        margin-top: 36px !important;

        .spinner {
            margin-top: 100px;
        }
    }

    .create-folder {
        position: fixed;
        bottom: 0%;
        right: 0%;
        z-index: 3;
        margin: 24px;
    }

    .flip-list-enter, .flip-list-leave-to	{
        opacity: 0;
    }

    .flip-list-leave-active {
        position: absolute;
    }

    .flip-list-move {
        transition: transform $anim-time;
    }
</style>
